import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/queries/useProfile";
import { Toaster, toast } from "sonner";
import ProcessingScreen from "@/components/ProcessingScreen";
import VoiceCapture from "@/components/capture/VoiceCapture";
import PhotoCapture, { type PhotoCaptureData } from "@/components/capture/PhotoCapture";
import type { ProcessorResult } from "@/lib/captureProcessor";

type ContributorStep = "ready" | "voice" | "photo" | "text" | "processing" | "result";
type MemberStep = "welcome" | "auth" | "accepting" | "done";
type GlobalStep = "loading" | "invalid";

interface InviteData {
  connectionId: string;
  childName: string;
  inviterNickname: string;
  role: "member" | "contributor" | "child";
  expiresAt: string | null;
  // Photo cap for one-off contributions follows the space owner's tier,
  // not the contributor's. Returned by get-invite-details; enforced on the
  // client here and (in chat 6) again server-side in process-memory.
  maxPhotosPerMemory: number;
  // Present when the invite was sent to a specific email address; absent for
  // link-only contributor invites. Drives which flow the contributor lands in.
  inviteeEmail: string | null;
}

// ── Main Component ──────────────────────────────────────────────────────────

const InvitePage = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [globalStep, setGlobalStep] = useState<GlobalStep | null>("loading");
  const [invite, setInvite] = useState<InviteData | null>(null);

  // Load invite on mount via edge function
  useEffect(() => {
    if (!token) { setGlobalStep("invalid"); return; }

    supabase.functions
      .invoke("get-invite-details", { body: { token } })
      .then(({ data, error }) => {
        if (error || !data) { setGlobalStep("invalid"); return; }
        const result = data as {
          connectionId: string;
          childName: string;
          invitedByName: string;
          role: string;
          expiresAt: string | null;
          maxPhotosPerMemory?: number;
          inviteeEmail?: string | null;
        };
        setInvite({
          connectionId: result.connectionId,
          childName: result.childName,
          inviterNickname: result.invitedByName,
          role: result.role as "member" | "contributor",
          expiresAt: result.expiresAt,
          maxPhotosPerMemory: result.maxPhotosPerMemory ?? 1,
          inviteeEmail: result.inviteeEmail ?? null,
        });
        setGlobalStep(null); // Clear global step, let role-specific flow take over
      });
  }, [token]);

  if (globalStep === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (globalStep === "invalid") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-sm text-center space-y-3">
          <h1 className="font-display text-2xl tracking-tight text-foreground">
            Invite not found
          </h1>
          <p className="text-sm text-muted-foreground">
            This invite link may have already been used or has expired.
          </p>
        </div>
        <Toaster position="top-center" />
      </div>
    );
  }

  if (!invite || !token) return null;

  if (invite.role === "member") {
    return <MemberFlow invite={invite} token={token} />;
  }

  if (invite.role === "child") {
    return <ChildFlow invite={invite} token={token} />;
  }

  // Email-invited contributors get the full account+app flow; link-only
  // contributors use the existing one-shot anonymous recording flow.
  if (invite.role === "contributor" && invite.inviteeEmail) {
    return <ContributorMemberFlow invite={invite} token={token} />;
  }

  return <ContributorFlow invite={invite} token={token} />;
};

// ── Member (co-parent) Flow ─────────────────────────────────────────────────

function MemberFlow({ invite, token }: { invite: InviteData; token: string }) {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [step, setStep] = useState<MemberStep>("welcome");
  const [accepting, setAccepting] = useState(false);
  const hasAccepted = useRef(false);

  // Co-parent's display name as it should appear in the tribe picker.
  // Prefilled from profiles.nickname; user can override before accepting.
  const [displayName, setDisplayName] = useState("");
  const hasPrefilledNickname = useRef(false);
  useEffect(() => {
    if (!profile?.nickname || hasPrefilledNickname.current) return;
    hasPrefilledNickname.current = true;
    const nick = profile.nickname.trim();
    if (nick) setDisplayName(nick);
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // If user is already logged in when they land here, skip straight to confirmation.
  // Guard with a ref so a failed accept can't re-trigger this transition.
  const hasMovedToAuth = useRef(false);
  useEffect(() => {
    if (!authLoading && user && step === "welcome" && !hasMovedToAuth.current) {
      hasMovedToAuth.current = true;
      setStep("auth");
    }
  }, [authLoading, user, step]);

  // After a new sign-in, move to confirmation screen (do NOT auto-accept —
  // let the user click the button so they see what they're joining).
  useEffect(() => {
    if (step !== "auth") return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          // New sign-in: stay on "auth" so the confirmation button is visible.
          // React will re-render with the updated `user` from useAuth.
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [step]);

  const acceptInvite = async () => {
    if (hasAccepted.current) return;
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      toast.error(t("error.invite.needName"));
      return;
    }
    hasAccepted.current = true;
    setStep("accepting");
    try {
      const { data, error } = await supabase.functions.invoke(
        "accept-coparent-invite",
        { body: { token, displayName: trimmedName } }
      );

      if (error || !data?.success) {
        const knownErrorCodes = [
          "already-member-of-another-space",
          "self-invite",
          "wrong-account",
        ];
        const message =
          data?.errorCode && knownErrorCodes.includes(data.errorCode) && data?.error
            ? data.error
            : t("error.invite.expired");
        toast.error(message);
        hasAccepted.current = false;
        setStep("auth");
        return;
      }

      setStep("done");
    } catch {
      toast.error(t("toast.genericError"));
      hasAccepted.current = false;
      setStep("auth");
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    hasMovedToAuth.current = false;
    setStep("welcome");
  };

  const handleGoogleAuth = async () => {
    setStep("auth");
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/invite/${token}` },
    });
  };

  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  const handleMagicLink = async () => {
    if (!magicEmail.trim()) return;
    const { error } = await supabase.auth.signInWithOtp({
      email: magicEmail.trim(),
      options: { emailRedirectTo: `${window.location.origin}/invite/${token}` },
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    setMagicSent(true);
  };

  // ── Accepting spinner ───────────────────────────────────────────────────
  if (step === "accepting") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6">
        <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Joining the fire…</p>
      </div>
    );
  }

  // ── Done (success) ──────────────────────────────────────────────────────
  if (step === "done") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 animate-page-enter">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Check className="h-7 w-7 text-primary" />
        </div>
        <div className="text-center space-y-2">
          <h1 className="font-display text-3xl tracking-tight text-foreground">
            You're in!
          </h1>
          <p className="text-base text-muted-foreground">
            Welcome to the tribe. Start capturing memories together.
          </p>
        </div>
        <Button className="w-full max-w-sm h-12 text-base" onClick={() => navigate("/", { replace: true })}>
          Open Mori
        </Button>
      </div>
    );
  }

  // ── Auth step (already logged in — confirm) ─────────────────────────────
  if (step === "auth" && user) {
    return (
      <PageWrapper>
        <div className="text-center space-y-2">
          <h1 className="font-display text-3xl tracking-tight text-foreground">
            Join as co-parent?
          </h1>
          <p className="text-base text-muted-foreground">
            Join{" "}
            <span className="font-medium text-foreground">{invite.childName}</span>'s
            fire as a co-parent.
          </p>
          <p className="text-xs text-muted-foreground">
            Signed in as <span className="font-medium">{user.email}</span>
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="member-display-name">What should we call you?</Label>
          <Input
            id="member-display-name"
            placeholder="Mum, Dad, Babi…"
            value={displayName}
            maxLength={80}
            onChange={(e) => setDisplayName(e.target.value)}
            className="h-12 text-base bg-card"
          />
        </div>

        <Button
          className="w-full h-12 text-base"
          onClick={acceptInvite}
          disabled={!displayName.trim()}
        >
          Accept invite
        </Button>
        <Button
          variant="ghost"
          className="w-full h-10 text-sm text-muted-foreground"
          onClick={handleSignOut}
        >
          Wrong account? Sign out
        </Button>
      </PageWrapper>
    );
  }

  // ── Auth step (not logged in — show auth options) ───────────────────────
  if (step === "auth") {
    return (
      <PageWrapper>
        <img
          src="/mori-logo.webp"
          alt="Mori"
          className="mx-auto w-full"
        />

        <div className="text-center space-y-2">
          <h1 className="font-display text-2xl tracking-tight text-foreground">
            Sign in to accept
          </h1>
          <p className="text-sm text-muted-foreground">
            Create an account or sign in to join{" "}
            <span className="font-medium text-foreground">{invite.childName}</span>'s fire.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {magicSent ? (
          <div className="rounded-xl border border-border bg-card px-5 py-4 text-center space-y-1">
            <p className="text-sm text-foreground font-medium">{t("invite.checkInbox")}</p>
            <p className="text-xs text-muted-foreground">
              We sent a one-click sign-in link to <span className="font-medium">{magicEmail}</span>. Click it to continue — no password needed.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="your@email.com"
              value={magicEmail}
              onChange={(e) => setMagicEmail(e.target.value)}
              className="h-12 text-base bg-card"
            />
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={handleMagicLink}
              disabled={!magicEmail.trim()}
            >
              Sign in
            </Button>
          </div>
        )}

        <Button
          variant="outline"
          className="w-full h-12 gap-3 text-base font-medium border-border"
          onClick={handleGoogleAuth}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </Button>
      </PageWrapper>
    );
  }

  // ── Welcome step ────────────────────────────────────────────────────────
  return (
    <PageWrapper>
      <div className="text-center space-y-2">
        <h1 className="font-display text-3xl tracking-tight text-foreground">
          You're invited
        </h1>
        <p className="text-base text-muted-foreground">
          <span className="font-medium text-foreground">{invite.inviterNickname}</span>{" "}
          has invited you as a co-parent for{" "}
          <span className="font-medium text-foreground">{invite.childName}</span>'s memories.
        </p>
      </div>

      <div className="space-y-2.5 rounded-2xl border border-border bg-card p-4">
        {[
          "Add voice memories, photos and drawings",
          `See all of ${invite.childName}'s memories`,
          t("invite.keepFireBurning"),
        ].map((feature) => (
          <div key={feature} className="flex items-start gap-2.5">
            <span className="text-primary mt-0.5 text-sm">✓</span>
            <span className="text-sm text-foreground">{feature}</span>
          </div>
        ))}
      </div>

      <div className="rounded-xl bg-[hsl(38_80%_95%)] border border-[hsl(38_60%_85%)] px-3.5 py-2.5 text-center">
        <p className="text-xs text-[hsl(38_50%_30%)]">
          🎁 Join now and get <span className="font-semibold">+500MB</span> bonus storage — free
        </p>
      </div>

      <Button className="w-full h-12 text-base" onClick={() => setStep("auth")}>
        Create your account 🔥
      </Button>
      <Button
        variant="ghost"
        className="w-full h-10 text-sm text-muted-foreground"
        onClick={() => setStep("auth")}
      >
        I already have an account — Sign in
      </Button>
    </PageWrapper>
  );
}

// ── Child Flow ──────────────────────────────────────────────────────────────

function ChildFlow({ invite, token }: { invite: InviteData; token: string }) {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<MemberStep>("welcome");
  const [accepting, setAccepting] = useState(false);
  const hasAccepted = useRef(false);

  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  // Already logged in → skip to confirm
  const hasMovedToAuth = useRef(false);
  useEffect(() => {
    if (!authLoading && user && step === "welcome" && !hasMovedToAuth.current) {
      hasMovedToAuth.current = true;
      setStep("auth");
    }
  }, [authLoading, user, step]);

  const acceptInvite = async () => {
    if (hasAccepted.current) return;
    hasAccepted.current = true;
    setAccepting(true);
    setStep("accepting");
    try {
      const { data, error } = await supabase.functions.invoke(
        "accept-coparent-invite",
        { body: { token } }
      );
      if (error || !data?.success) {
        toast.error(t("error.invite.expired"));
        hasAccepted.current = false;
        setStep("auth");
        return;
      }
      setStep("done");
    } catch {
      toast.error(t("toast.genericError"));
      hasAccepted.current = false;
      setStep("auth");
    } finally {
      setAccepting(false);
    }
  };

  const handleGoogleAuth = async () => {
    setStep("auth");
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/invite/${token}` },
    });
  };

  const handleMagicLink = async () => {
    if (!magicEmail.trim()) return;
    const { error } = await supabase.auth.signInWithOtp({
      email: magicEmail.trim(),
      options: { emailRedirectTo: `${window.location.origin}/invite/${token}` },
    });
    if (error) { toast.error(error.message); return; }
    setMagicSent(true);
  };

  // Accepting spinner
  if (step === "accepting") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6">
        <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Joining Mori…</p>
      </div>
    );
  }

  // Done
  if (step === "done") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 animate-page-enter">
        <div className="text-6xl">🐿️</div>
        <div className="text-center space-y-2">
          <h1 className="font-display text-3xl tracking-tight text-foreground">
            You're in, {invite.childName}!
          </h1>
          <p className="text-base text-muted-foreground">
            Mori is ready. Start capturing your memories.
          </p>
        </div>
        <Button className="w-full max-w-sm h-12 text-base" onClick={() => navigate("/", { replace: true })}>
          Open Mori 🌰
        </Button>
      </div>
    );
  }

  // Auth step — already logged in
  if (step === "auth" && user) {
    return (
      <PageWrapper>
        <div className="text-6xl text-center">🐿️</div>
        <div className="text-center space-y-2">
          <h1 className="font-display text-2xl tracking-tight text-foreground">
            Ready to join?
          </h1>
          <p className="text-sm text-muted-foreground">
            Tap below and you're in as{" "}
            <span className="font-medium text-foreground">{invite.childName}</span>.
          </p>
        </div>
        <Button className="w-full h-12 text-base" onClick={acceptInvite} disabled={accepting}>
          Join Mori →
        </Button>
      </PageWrapper>
    );
  }

  // Auth step — not logged in (sign-up / sign-in)
  if (step === "auth") {
    return (
      <PageWrapper>
        <div className="text-6xl text-center">🐿️</div>
        <div className="text-center space-y-2">
          <h1 className="font-display text-2xl tracking-tight text-foreground">
            Create your account
          </h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{invite.inviterNickname}</span>{" "}
            set up Mori just for you,{" "}
            <span className="font-medium text-foreground">{invite.childName}</span>.
          </p>
        </div>

        {magicSent ? (
          <div className="rounded-xl border border-border bg-card px-5 py-4 text-center space-y-1">
            <p className="text-sm font-medium text-foreground">{t("invite.checkInbox")}</p>
            <p className="text-xs text-muted-foreground">
              We sent a sign-in link to{" "}
              <span className="font-medium">{magicEmail}</span>.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="your@email.com"
              value={magicEmail}
              onChange={(e) => setMagicEmail(e.target.value)}
              className="h-12 text-base bg-card"
            />
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={handleMagicLink}
              disabled={!magicEmail.trim()}
            >
              Sign in with email
            </Button>
          </div>
        )}

        <Button
          variant="outline"
          className="w-full h-12 gap-3 text-base font-medium border-border"
          onClick={handleGoogleAuth}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </Button>
      </PageWrapper>
    );
  }

  // Welcome step — child-friendly landing
  return (
    <PageWrapper>
      <div className="text-center space-y-1">
        <div className="text-6xl">🐿️</div>
        <p className="text-xs font-medium text-muted-foreground tracking-widest uppercase mt-2">Mori</p>
      </div>

      <div className="text-center space-y-2">
        <h1 className="font-display text-3xl tracking-tight text-foreground">
          Hi {invite.childName}! 👋
        </h1>
        <p className="text-base text-muted-foreground">
          <span className="font-medium text-foreground">{invite.inviterNickname}</span>{" "}
          set up Mori just for you.
        </p>
      </div>

      <div className="space-y-2.5 rounded-2xl border border-border bg-card p-4">
        {[
          { emoji: "🎨", text: "Draw and create" },
          { emoji: "🎤", text: "Record voice messages" },
          { emoji: "📸", text: "Take photos" },
          { emoji: "❤️", text: "Share with your family" },
        ].map(({ emoji, text }) => (
          <div key={text} className="flex items-center gap-2.5">
            <span className="text-lg">{emoji}</span>
            <span className="text-sm text-foreground">{text}</span>
          </div>
        ))}
      </div>

      <Button className="w-full h-12 text-base" onClick={() => setStep("auth")}>
        Join Mori →
      </Button>
    </PageWrapper>
  );
}

// ── Contributor Member Flow (email-invited contributors) ────────────────────
//
// Near-clone of MemberFlow for contributors who were invited via email.
// Difference: welcome copy is "contribute memories for {child}", and on
// success we navigate directly to "/" without a separate done screen.

function ContributorMemberFlow({ invite, token }: { invite: InviteData; token: string }) {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [step, setStep] = useState<MemberStep>("welcome");
  const hasAccepted = useRef(false);

  const [displayName, setDisplayName] = useState("");
  const hasPrefilledNickname = useRef(false);
  useEffect(() => {
    if (!profile?.nickname || hasPrefilledNickname.current) return;
    hasPrefilledNickname.current = true;
    const nick = profile.nickname.trim();
    if (nick) setDisplayName(nick);
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMovedToAuth = useRef(false);
  useEffect(() => {
    if (!authLoading && user && step === "welcome" && !hasMovedToAuth.current) {
      hasMovedToAuth.current = true;
      setStep("auth");
    }
  }, [authLoading, user, step]);

  useEffect(() => {
    if (step !== "auth") return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {});
    return () => subscription.unsubscribe();
  }, [step]);

  const acceptInvite = async () => {
    if (hasAccepted.current) return;
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      toast.error(t("error.invite.needName"));
      return;
    }
    hasAccepted.current = true;
    setStep("accepting");
    try {
      const { data, error } = await supabase.functions.invoke(
        "accept-coparent-invite",
        { body: { token, displayName: trimmedName } }
      );

      if (error || !data?.success) {
        const knownErrorCodes = ["already-member-of-another-space", "self-invite", "wrong-account"];
        const message =
          data?.errorCode && knownErrorCodes.includes(data.errorCode) && data?.error
            ? data.error
            : t("error.invite.expired");
        toast.error(message);
        hasAccepted.current = false;
        setStep("auth");
        return;
      }

      // If the user already had their own populated space, accept-coparent-invite
      // leaves `profiles.active_space_id` pointing there (keptInOwnSpace=true)
      // so they don't lose sight of their own data. Nudge them to the header
      // chip so they know how to pivot to the inviter's space.
      if (data.keptInOwnSpace) {
        toast.success(
          t("invite.contributor.keptInOwnSpace", { family: invite.childName }),
        );
      }

      navigate("/", { replace: true });
    } catch {
      toast.error(t("toast.genericError"));
      hasAccepted.current = false;
      setStep("auth");
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    hasMovedToAuth.current = false;
    setStep("welcome");
  };

  const handleGoogleAuth = async () => {
    setStep("auth");
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/invite/${token}` },
    });
  };

  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  const handleMagicLink = async () => {
    if (!magicEmail.trim()) return;
    const { error } = await supabase.auth.signInWithOtp({
      email: magicEmail.trim(),
      options: { emailRedirectTo: `${window.location.origin}/invite/${token}` },
    });
    if (error) { toast.error(error.message); return; }
    setMagicSent(true);
  };

  if (step === "accepting") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6">
        <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Joining the fire…</p>
      </div>
    );
  }

  if (step === "auth" && user) {
    return (
      <PageWrapper>
        <div className="text-center space-y-2">
          <h1 className="font-display text-3xl tracking-tight text-foreground">
            Contribute memories?
          </h1>
          <p className="text-base text-muted-foreground">
            Sign in to contribute memories for{" "}
            <span className="font-medium text-foreground">{invite.childName}</span>.
          </p>
          <p className="text-xs text-muted-foreground">
            Signed in as <span className="font-medium">{user.email}</span>
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="contributor-member-display-name">What should we call you?</Label>
          <Input
            id="contributor-member-display-name"
            placeholder="Grandma, Martin, Coach Jana…"
            value={displayName}
            maxLength={80}
            onChange={(e) => setDisplayName(e.target.value)}
            className="h-12 text-base bg-card"
          />
        </div>

        <Button
          className="w-full h-12 text-base"
          onClick={acceptInvite}
          disabled={!displayName.trim()}
        >
          Accept invite
        </Button>
        <Button
          variant="ghost"
          className="w-full h-10 text-sm text-muted-foreground"
          onClick={handleSignOut}
        >
          Wrong account? Sign out
        </Button>
      </PageWrapper>
    );
  }

  if (step === "auth") {
    return (
      <PageWrapper>
        <img src="/mori-logo.webp" alt="Mori" className="mx-auto w-full" />

        <div className="text-center space-y-2">
          <h1 className="font-display text-2xl tracking-tight text-foreground">
            Sign in to accept
          </h1>
          <p className="text-sm text-muted-foreground">
            Sign in to contribute memories for{" "}
            <span className="font-medium text-foreground">{invite.childName}</span>.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {magicSent ? (
          <div className="rounded-xl border border-border bg-card px-5 py-4 text-center space-y-1">
            <p className="text-sm text-foreground font-medium">{t("invite.checkInbox")}</p>
            <p className="text-xs text-muted-foreground">
              We sent a one-click sign-in link to <span className="font-medium">{magicEmail}</span>. Click it to continue — no password needed.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="your@email.com"
              value={magicEmail}
              onChange={(e) => setMagicEmail(e.target.value)}
              className="h-12 text-base bg-card"
            />
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={handleMagicLink}
              disabled={!magicEmail.trim()}
            >
              Sign in
            </Button>
          </div>
        )}

        <Button
          variant="outline"
          className="w-full h-12 gap-3 text-base font-medium border-border"
          onClick={handleGoogleAuth}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </Button>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="text-center space-y-2">
        <h1 className="font-display text-3xl tracking-tight text-foreground">
          You're invited
        </h1>
        <p className="text-base text-muted-foreground">
          <span className="font-medium text-foreground">{invite.inviterNickname}</span>{" "}
          has invited you to contribute memories for{" "}
          <span className="font-medium text-foreground">{invite.childName}</span>.
        </p>
      </div>

      <div className="space-y-2.5 rounded-2xl border border-border bg-card p-4">
        {[
          "Add voice memories, photos and drawings",
          `Contribute to ${invite.childName}'s story`,
          t("invite.keepFireBurning"),
        ].map((feature) => (
          <div key={feature} className="flex items-start gap-2.5">
            <span className="text-primary mt-0.5 text-sm">✓</span>
            <span className="text-sm text-foreground">{feature}</span>
          </div>
        ))}
      </div>

      <Button className="w-full h-12 text-base" onClick={() => setStep("auth")}>
        Create your account 🔥
      </Button>
      <Button
        variant="ghost"
        className="w-full h-10 text-sm text-muted-foreground"
        onClick={() => setStep("auth")}
      >
        I already have an account — Sign in
      </Button>
    </PageWrapper>
  );
}

// ── Contributor Flow ─────────────────────────────────────────────────────────

function ContributorFlow({ invite, token }: { invite: InviteData; token: string }) {
  const { t } = useTranslation();
  const [step, setStep] = useState<ContributorStep>("ready");
  const [contributorName, setContributorName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [processBody, setProcessBody] = useState<Record<string, unknown> | null>(null);
  const [aiResult, setAiResult] = useState<{
    title: string | null;
    chapter: string | null;
    mori_comment: string | null;
  } | null>(null);

  const upsellMessage = useMemo(() => {
    const messages = [
      `Want to keep adding memories for ${invite.childName}? Mori is free to join at meetmori.app.`,
      "Every family deserves a vault. Start yours free — no credit card needed.",
      "Mori turns everyday moments into stories your family will treasure forever.",
      "You can add voice notes, photos, and drawings any time. Free at meetmori.app.",
      "Did you know Mori also writes the story for you? Try it with your own memories.",
      "Capture birthdays, firsts, and the little moments before they fade — free at meetmori.app.",
      "Thousands of families are building their story with Mori. Yours could be next.",
      "Mori keeps memories safe and turns them into a keepsake your family can read together.",
      "Join free and start your own vault — Mori will write the first story for you.",
      "Family memories are priceless. Keeping them doesn't have to cost a thing — meetmori.app.",
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  }, [invite.childName]);

  const baseName = contributorName.trim() || "Guest";

  // Called by both VoiceCapture steps (voice_moment + general_memory/written)
  const handleVoiceComplete = async (result: {
    audioBlob?: Blob;
    audioDurationSeconds?: number;
    writtenText?: string;
    contextMode?: "voice" | "written";
    contextText?: string;
  }) => {
    if (result.writtenText) {
      setProcessBody({
        invite_token: token,
        contributor_name: baseName,
        memory_type: "general_memory",
        context_text: result.writtenText,
      });
      setStep("processing");
      return;
    }

    if (!result.audioBlob) return;

    setUploading(true);
    try {
      const path = `contributors/${token}/${Date.now()}.webm`;
      const { error } = await supabase.storage
        .from("audio-memories")
        .upload(path, result.audioBlob, { contentType: "audio/webm" });
      if (error) throw new Error(error.message);

      const body: Record<string, unknown> = {
        invite_token: token,
        contributor_name: baseName,
        memory_type: "voice_moment",
        audio_path: path,
        audio_duration_seconds: result.audioDurationSeconds ?? 0,
      };
      if (result.contextText) body.context_text = result.contextText;

      setProcessBody(body);
      setStep("processing");
    } catch {
      toast.error(t("error.upload.failed"));
    } finally {
      setUploading(false);
    }
  };

  // Called by PhotoCapture after the user picks + previews the image
  const handlePhotoSave = async (photoData: PhotoCaptureData) => {
    setUploading(true);
    try {
      const ext = photoData.imageFile.type.includes("png") ? "png" : "jpg";
      const path = `contributors/${token}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from("memory-images")
        .upload(path, photoData.imageFile, { contentType: photoData.imageFile.type });
      if (error) throw new Error(error.message);

      const body: Record<string, unknown> = {
        invite_token: token,
        contributor_name: baseName,
        memory_type: "photo",
        image_path: path,
        photo_count: 1,
      };
      if (photoData.contextText) body.context_text = photoData.contextText;

      setProcessBody(body);
      setStep("processing");
    } catch {
      toast.error(t("error.upload.failed"));
    } finally {
      setUploading(false);
    }
  };

  const contributorProcessor = async (): Promise<ProcessorResult> => {
    if (!processBody) throw new Error("No capture data");

    const { data, error: fnError } = await supabase.functions.invoke("process-memory", { body: processBody });
    if (fnError) throw new Error(`Could not save memory: ${fnError.message}`);

    const d = data as { memoryId?: string; title?: string; chapter?: string; mori_comment?: string } | null;
    setAiResult({
      title: d?.title ?? null,
      chapter: d?.chapter ?? null,
      mori_comment: d?.mori_comment ?? null,
    });

    return {
      memoryId: d?.memoryId ?? "contributor",
      memoryType: String(processBody.memory_type ?? "voice"),
      contentLibraryId: null,
      skipFanout: true,
      forcedSkip: (d as { forced_skip?: boolean } | null)?.forced_skip === true,
    };
  };

  // ── Upload spinner (between capture complete and ProcessingScreen) ─────────
  if (uploading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
        <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // ── Processing ────────────────────────────────────────────────────────────
  if (step === "processing" && processBody) {
    return (
      <ProcessingScreen
        processor={contributorProcessor}
        onComplete={() => setStep("result")}
        onClose={() => {
          setProcessBody(null);
          setStep("ready");
        }}
      />
    );
  }

  // ── Result ────────────────────────────────────────────────────────────────
  if (step === "result") {
    return (
      <div className="relative flex min-h-screen items-start justify-center overflow-hidden bg-background px-6 py-10 animate-page-enter">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary/[0.06] animate-float" />
          <div className="absolute top-1/3 -left-32 h-96 w-96 rounded-full bg-accent/[0.07] animate-float-slow" />
        </div>
        <div className="relative z-10 w-full max-w-sm space-y-6">
          <div className="text-center space-y-1">
            <div className="text-4xl">🎉</div>
            <h1 className="font-display text-2xl tracking-tight text-foreground">
              Memory saved!
            </h1>
            <p className="text-base text-muted-foreground">
              Thank you{contributorName ? `, ${contributorName}` : ""}!
            </p>
          </div>

          {aiResult?.title && (
            <h2 className="font-display text-xl tracking-tight text-foreground text-center">
              {aiResult.title}
            </h2>
          )}

          {aiResult?.chapter && (
            <p
              className="text-sm text-foreground"
              style={{ fontFamily: "Georgia, serif", lineHeight: 1.7 }}
            >
              {aiResult.chapter}
            </p>
          )}

          {aiResult?.mori_comment && (
            <div
              className="relative rounded-2xl px-4 py-3 pl-12"
              style={{
                backgroundColor: "hsl(var(--category-funny) / 0.55)",
                border: "1px solid hsl(var(--category-funny-text) / 0.2)",
              }}
            >
              <span
                aria-hidden
                className="absolute left-3 top-2.5 text-xl leading-none select-none"
              >
                🐿️
              </span>
              <p
                className="text-sm italic"
                style={{
                  color: "hsl(var(--category-funny-text))",
                  lineHeight: 1.55,
                  fontFamily: "Georgia, serif",
                }}
              >
                {aiResult.mori_comment}
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center">
            ⏳ {invite.inviterNickname} will review this memory before it appears in the vault.
          </p>

          <div className="rounded-xl bg-[hsl(38_80%_95%)] border border-[hsl(38_60%_85%)] px-4 py-4">
            <p className="text-sm text-[hsl(38_50%_30%)]">{upsellMessage}</p>
          </div>

          <Link to="/auth">
            <Button className="w-full h-12 text-base">
              Create your free Mori account →
            </Button>
          </Link>
        </div>
        <Toaster position="top-center" />
      </div>
    );
  }

  // ── Voice capture — reuses the same VoiceCapture as the FAB flow ──────────
  if (step === "voice") {
    return (
      <div className="min-h-screen bg-background">
        <VoiceCapture
          captureType="voice_moment"
          onComplete={handleVoiceComplete}
          onCancel={() => setStep("ready")}
          onBack={() => setStep("ready")}
        />
        <Toaster position="top-center" />
      </div>
    );
  }

  // ── Photo capture — reuses the same PhotoCapture as the FAB flow ──────────
  if (step === "photo") {
    return (
      <div className="min-h-screen bg-background">
        <PhotoCapture
          memoryType="photo"
          onSave={handlePhotoSave}
          onCancel={() => setStep("ready")}
        />
        <Toaster position="top-center" />
      </div>
    );
  }

  // ── Story capture — VoiceCapture in general_memory mode (Speak/Write tabs).
  //    Unauthenticated contributors have no entitlements, so canVoiceContext
  //    is falsy and the Write tab is selected by default — exactly what we want.
  if (step === "text") {
    return (
      <div className="min-h-screen bg-background">
        <VoiceCapture
          captureType="general_memory"
          onComplete={handleVoiceComplete}
          onCancel={() => setStep("ready")}
          onBack={() => setStep("ready")}
        />
        <Toaster position="top-center" />
      </div>
    );
  }

  // ── Ready (name input + type picker) ─────────────────────────────────────
  return (
    <PageWrapper>
      <div className="text-center space-y-2">
        <h1 className="font-display text-3xl tracking-tight text-foreground">
          Memory Vault
        </h1>
        <p className="text-base text-muted-foreground">
          <span className="font-medium text-foreground">{invite.inviterNickname}</span>{" "}
          has invited you to contribute memories for{" "}
          <span className="font-medium text-foreground">{invite.childName}</span>!
        </p>
      </div>

      <div className="rounded-xl bg-[hsl(38_80%_95%)] border border-[hsl(38_60%_85%)] px-3.5 py-2.5 text-center">
        <p className="text-xs text-[hsl(38_50%_30%)]">
          🎁 Join now and get <span className="font-semibold">+500MB</span> bonus storage — free
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="contributor-name">What should we call you?</Label>
        <Input
          id="contributor-name"
          placeholder="Grandma, Martin, Coach Jana…"
          value={contributorName}
          onChange={(e) => setContributorName(e.target.value)}
          className="h-12 text-base bg-card"
        />
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">What kind of memory?</p>
        <div className="grid grid-cols-3 gap-2">
          <button
            disabled={!contributorName.trim()}
            onClick={() => setStep("voice")}
            className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-3 py-4 text-sm font-medium text-foreground transition-all hover:border-primary hover:bg-primary/5 disabled:opacity-40 disabled:pointer-events-none"
          >
            <span className="text-2xl leading-none">📼</span>
            Voice
          </button>
          <button
            disabled={!contributorName.trim()}
            onClick={() => setStep("photo")}
            className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-3 py-4 text-sm font-medium text-foreground transition-all hover:border-primary hover:bg-primary/5 disabled:opacity-40 disabled:pointer-events-none"
          >
            <span className="text-2xl leading-none">📷</span>
            Photo
          </button>
          <button
            disabled={!contributorName.trim()}
            onClick={() => setStep("text")}
            className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-3 py-4 text-sm font-medium text-foreground transition-all hover:border-primary hover:bg-primary/5 disabled:opacity-40 disabled:pointer-events-none"
          >
            <span className="text-2xl leading-none">✍️</span>
            Story
          </button>
        </div>
        {!contributorName.trim() && (
          <p className="text-xs text-muted-foreground text-center">
            Enter your name above first
          </p>
        )}
      </div>
    </PageWrapper>
  );
}

// ── Shared page wrapper ─────────────────────────────────────────────────────

function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6 animate-page-enter">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary/[0.06] animate-float" />
        <div className="absolute top-1/3 -left-32 h-96 w-96 rounded-full bg-accent/[0.07] animate-float-slow" />
        <div className="absolute -bottom-16 right-10 h-56 w-56 rounded-full bg-primary/[0.04] animate-float-slower" />
      </div>
      <div className="relative z-10 w-full max-w-sm space-y-8">
        {children}
      </div>
      <Toaster position="top-center" />
    </div>
  );
}

export default InvitePage;
